import { useGTMDispatch } from '@elgorditosalsero/react-gtm-hook'
import type { DatePickerProps } from 'antd'
import { DatePicker, notification } from 'antd'
import dayjs from 'dayjs'
import moment from 'moment'
import React, { useEffect, useState } from 'react'
import swal from 'sweetalert'
import CompanyLogo from '../../assets/afbc.png'
import Side4 from '../../assets/investing-signup-images/cans_1090.jpeg'
import Button from '../../components/Button'
import ErrorMessage from '../../components/ErrorMessage'
import FormLabel from '../../components/FormLabel'
import InputField from '../../components/InputField'
import LoaderModal from '../../components/Modals/LoaderModal'
import Stepper from '../../components/Stepper'
import { useRiseUpAPIV1 } from '../../hooks/useRiseUpAPIV1'
import { IFormProps, InvestingPersonEnum, RetirementAccountEnum } from '../../interfaces'

import MessageModal from 'components/Modals/MessageModal'
import { RiseUpErrors } from 'constants/errors'
import useErrorMessage from 'hooks/useErrorMessage'
import { useGTMData } from 'hooks/useGTMData'
import { useUTMTracking } from 'hooks/useUTMTracking'
import _ from 'lodash'
import queryString from 'query-string'
import 'react-datepicker/dist/react-datepicker.css'
import ReactGA from 'react-ga4'
import { Controller } from 'react-hook-form'
import { useLocation, useNavigate } from 'react-router-dom'
import { getModifiedUrlWithStep, returnFormErrorString } from 'utils'
import { consoleLog } from 'utils/simpleLogger'

const SocialSecurityNum = (props: IFormProps) => {
  const [isLoading, setIsLoading] = useState(false)
  const [birthdayValidation, setBirthdayValidation] = useState({
    primary: false,
    secondary: false,
  })

  const { updateOnboardProgress } = useRiseUpAPIV1()
  const sendDataToGTM = useGTMDispatch()
  const { errorMessage, getFriendlyErrorMessage, setErrorMessage } = useErrorMessage()
  const gtmData = useGTMData(props.getValues)
  const {
    getFormattedUTMParams, // Helper to generate a complete GTM event object
  } = useUTMTracking()
  const navigate = useNavigate()
  const location = useLocation()

  const disabledDate = (current: moment.Moment) => {
    return current > moment().subtract(21, 'years')
  }

  const onChangeBirthdayPrimary: DatePickerProps['onChange'] = (date, dateString) => {
    if (typeof dateString === 'string') {
      let year = moment().diff(new Date(dateString), 'years')

      consoleLog('Birthday primary', dateString)
      if (moment(dateString).isValid()) {
        if (year < 21) {
          swal({
            title: '',
            text: 'You must be at least 21 years old to invest.',
            icon: 'error',
          })
        }
        props.setValue('personalInfo.birthday', moment(dateString).format('YYYY-MM-DD'))
        // props.setValue('personalInfo.birthday', dayjs(String(dateString)))
        props.validateFields('personalInfo.birthday')
      }
    } else {
      console.error('Unexpected type for dateString:', dateString)
    }
  }

  const onBlurBirthdayChange = (e: any, name: string) => {
    const dateString = e.target.value
    if (typeof dateString === 'string') {
      let year = moment().diff(new Date(dateString), 'years')
      if (moment(dateString).isValid()) {
        if (year < 21) {
          swal({
            title: '',
            text: 'You must be at least 21 years old to invest.',
            icon: 'error',
          })
        }
        props.setValue(name, dayjs(dateString))
        props.validateFields(name)
      } else if (!moment(dateString).isValid()) {
        if (!props.getValues('personalInfo.birthday')) {
          props.setValue(name, '')
          props.validateFields(name)
        } else {
          props.getValues('personalInfo.birthday')
          props.validateFields(name)
        }
        // swal({
        //   title: '',
        //   text: 'Invalid date input. Date back to previous date.',
        //   icon: 'error',
        // })
      }
    }
  }
  const onChangeBirthdaySecondary: DatePickerProps['onChange'] = (date, dateString) => {
    if (typeof dateString === 'string') {
      let year = moment().diff(new Date(dateString), 'years')

      if (moment(dateString).isValid()) {
        if (year < 21) {
          swal({
            title: '',
            text: 'You must be at least 21 years old to invest.',
            icon: 'error',
          })
        }

        props.setValue('secondary.personalInfo.birthday', moment(dateString).format('YYYY-MM-DD'))
        // props.setValue('secondary.personalInfo.birthday', dayjs(String(dateString)))
        props.validateFields('secondary.personalInfo.birthday')
      }
    }
  }

  const validatePage = async (): Promise<boolean> => {
    if (props.getValues('investingProfile.option') === 'JOINTLY') {
      let jointly: any[] = [
        'personalInfo.birthday',
        'financialInfo.ssn',
        'secondary.personalInfo.birthday',
        'secondary.financialInfo.ssn',
      ]

      // validate all fields using API trigger
      await props.validateFields(jointly)
      // get all fields value for null check (important to validate hidden form fields)
      jointly = props.getValues(jointly)

      return (
        jointly.includes(undefined) ||
        jointly.includes(null) ||
        jointly.includes('') ||
        jointly[1].includes('#') ||
        Boolean(!props.isValid) ||
        !props.getValues('personalInfo')
      )
    } else {
      let primary: any[] = ['personalInfo.birthday', 'financialInfo.ssn']

      // validate all fields using API trigger
      await props.validateFields(primary)
      // get all fields value for null check (important to validate hidden form fields)
      primary = props.getValues(primary)

      return (
        primary.includes(undefined) ||
        primary.includes(null) ||
        primary.includes('') ||
        primary[1].includes('#') ||
        Boolean(!props.isValid)
      )
    }
  }

  const isSubmitDisabled = () => {
    const fields =
      props.getValues('investingProfile.option') === 'JOINTLY'
        ? [
            'personalInfo.birthday',
            'financialInfo.ssn',
            'secondary.personalInfo.birthday',
            'secondary.financialInfo.ssn',
          ]
        : ['personalInfo.birthday', 'financialInfo.ssn']
    let formValues = props.getValues(fields)
    if (
      formValues?.includes('') ||
      formValues[1]?.includes('#') ||
      (props.getValues('investingProfile.option') === 'JOINTLY' && formValues[3]?.includes('#'))
    ) {
      return true
    }
    return false
  }

  const flattenObject = (obj: any): any => {
    const arr = { ...obj } // copy

    if (obj?.personalInfo || obj.personalInfo?.length) {
      delete arr.personalInfo
      // delete unneccessary arrays
      return { ...arr, ...obj.personalInfo }
    }
    if (obj?.financialInfo || obj.financialInfo?.length) {
      delete arr.financialInfo
      // delete unneccessary arrays
      return { ...arr, ...obj.financialInfo }
    }
    // return copied, but pass original to flatMapDeep
    return arr
  }

  const validateFields = async () => {
    if ((await validatePage()) && !_.isEmpty(await props.errors)) {
      consoleLog('SSS validation error', props.errors)
      notification.warning({
        duration: 2,
        message: 'Form has error',
        placement: 'topRight',
        description: returnFormErrorString(
          flattenObject(
            props?.errors?.secondary
              ? props?.errors?.secondary
              : props?.errors?.financialInfo
                ? props?.errors?.financialInfo
                : props?.errors,
          ),
        ),
        key: 'form-notif',
      })
      return
    } else {
      handleSubmit()
    }
  }

  const handleSubmit = async () => {
    setIsLoading(true)

    try {
      props.setValue(
        'financialInfo.ssn',
        props.getValues('addressInfo.country') === 'usa'
          ? props.getValues('financialInfo.ssn').replace(/[^0-9]+/g, '')
          : props.getValues('financialInfo.ssn'),
      )
      const data = {
        ...props.getValues(),
        lastCompletedStage: '5',
      }
      const updateOnboardProgressRes = await updateOnboardProgress(data)
      const formattedUTMParams = getFormattedUTMParams()
      const pathname = location.pathname
      const currentParams = queryString.parse(location.search)
      const updatedUrl = getModifiedUrlWithStep(pathname, currentParams, 'investment_amount')

      if (updateOnboardProgressRes.status === 201 || updateOnboardProgressRes.status === 200) {
        sendDataToGTM({
          event: 'funnel_step',
          step_title: 'SOCIAL_SECURITY_NUM',
          ...gtmData,
          ...formattedUTMParams,
        })
        navigate(updatedUrl)
        props.nextStep('5')
      }
    } catch (error: any) {
      ReactGA.event({
        category: 'SSN page',
        action: `SSN page error`,
        label: JSON.stringify(error),
      })
      setIsLoading(false)
      // catch the duplicate errors
      if (error.response.data === RiseUpErrors.DuplicatePhoneNumber) {
        setErrorMessage(RiseUpErrors.DuplicatePhoneNumber + ' Please update the phone number.')
        return
      }
      if (error.response.data === RiseUpErrors.DuplicateSSN) {
        setErrorMessage(RiseUpErrors.DuplicateSSN + ' Please update the ssn.')
        return
      }
      swal({
        title: 'Error!',
        text: 'Please check if your details are valid and correct. If error persists, contact us.',
        icon: 'error',
      })
    }
  }
  useEffect(() => {
    if (props.getValues('investingProfile.option') === InvestingPersonEnum.JOINTLY) {
      if (
        props.getValues('secondary.personalInfo.birthday') !== '' &&
        props.getValues('secondary.personalInfo.birthday')
      ) {
        setBirthdayValidation({
          primary: true,
          secondary: true,
        })
      } else {
        setBirthdayValidation({
          primary: false,
          secondary: false,
        })
        props.setValue('secondary.personalInfo.birthday', '')
      }
      if (props.getValues('secondary.financialInfo.ssn') === '' || !props.getValues('secondary.financialInfo.ssn')) {
        props.setValue('secondary.financialInfo.ssn', '')
      }
    } else {
      if (props.getValues('personalInfo.birthday') !== '' && props.getValues('personalInfo.birthday')) {
        setBirthdayValidation({
          primary: true,
          secondary: true,
        })
      } else {
        setBirthdayValidation({
          primary: false,
          secondary: false,
        })
      }
    }
  }, []) // eslint-disable-line

  // useEffect(() => {
  //   sendDataToGTM({
  //     event: 'funnel_step',
  //     step_title: 'SOCIAL_SECURITY_NUM',
  //     ...gtmData,
  //   })
  // }, [])

  return (
    <div className="flex h-screen w-full flex-col md:flex-row md:justify-start">
      <div className="mt-10 flex flex-col sm:w-screen md:mt-0 md:min-h-screen md:w-[45%] lg:w-[40%] xl:w-[35%]">
        <div className="relative w-screen px-10 sm:h-auto sm:px-16 md:hidden">
          <img className="w-screen" src={CompanyLogo} alt="CompanyLogo" />
        </div>
        <div className="relative hidden flex-grow md:flex">
          <img className="flex h-screen w-full" src={Side4} alt="Side4" />
        </div>
      </div>

      <div className="flex flex-col p-10 sm:w-[100%] md:w-[55%] lg:h-screen lg:w-[60%] lg:overflow-y-scroll xl:w-[65%]">
        {/* <div className="justify-center mb-10 sm:hidden lg:flex">
          <div className="w-1/2">
            <img className="w-screen" src={CompanyLogo} alt="CompanyLogo" />
          </div>
        </div> */}

        <div className="md:my-3">
          <Stepper current={props.currentStage} />
        </div>

        <div className="py-4 text-left text-2xl font-semibold text-gray-900 md:py-12 md:pb-10 md:text-4xl">
          Just a few more details...
        </div>
        {props.getValues('investingProfile.option') === InvestingPersonEnum.JOINTLY ? (
          <>
            <div className={`mb-2 mt-4 text-xl font-semibold md:mb-8 md:font-bold`}>First Investor</div>
          </>
        ) : null}
        <div>
          <div className="my-4 mr-4 w-full lg:my-0">
            <FormLabel
              labelName={`${
                props.getValues('investingProfile.option') === InvestingPersonEnum.IN_BEHALF_OF_AN_ENTITY
                  ? 'Authorized Person'
                  : props.getValues('investingProfile.option') === InvestingPersonEnum.RETIREMENT &&
                      props.getValues('investingProfile.retirementAccountType') ===
                        RetirementAccountEnum.RETIREMENT_ACCOUNT
                    ? 'Retirement Account Holder'
                    : 'Your'
              } Date of Birth`}
            />

            <Controller
              control={props.control}
              name={'personalInfo.birthday'}
              rules={{
                required: true,
                validate: {
                  validateBday: (value: string) => moment().diff(new Date(value), 'years') >= 21,
                  invalidDate: (value: string) => moment(value).isValid(),
                },
              }}
              render={({ field }: any) => (
                <DatePicker
                  {...field}
                  format={'MM/DD/YYYY'}
                  onChange={onChangeBirthdayPrimary}
                  // onOpenChange={() => props.clearErrors('personalInfo.birthday')}
                  // onBlur={(e) => onBlurBirthdayChange(e, 'personalInfo.birthday')}
                  // onFocus={(e) => onBlurBirthdayChange(e, 'personalInfo.birthday')}
                  allowClear={false}
                  disabledDate={disabledDate}
                  status={
                    props.errors?.personalInfo?.birthday?.type === 'required' ||
                    props.errors?.personalInfo?.birthday?.type === 'invalidDate' ||
                    props.errors?.personalInfo?.birthday?.type === 'validateBday'
                      ? 'error'
                      : ''
                  }
                  value={
                    !_.isEmpty(props.getValues('personalInfo.birthday'))
                      ? dayjs(props.getValues('personalInfo.birthday'))
                      : dayjs().subtract(21, 'years')
                  }
                  className="h-[42px] border-gray-300 bg-gray-50"
                />
              )}
            />
            {props.errors?.personalInfo?.birthday?.type === 'required' && (
              <ErrorMessage message={'Birthday is required.'} />
            )}
            {props.errors?.personalInfo?.birthday?.type === 'validateBday' && (
              <ErrorMessage message={'Invalid date of birth. Must be at least 21 years old.'} />
            )}
            {props.errors?.personalInfo?.birthday?.type === 'invalidDate' && (
              <ErrorMessage message={'Invalid date input.'} />
            )}
          </div>

          {props.getValues('investingProfile.option') === InvestingPersonEnum.RETIREMENT ? (
            <FormLabel
              labelName={`${
                props.getValues('investingProfile.retirementAccountType') === RetirementAccountEnum.RETIREMENT_ACCOUNT
                  ? 'Retirement Account Holder'
                  : 'Your'
              }  ${
                (props.getValues('addressInfo.country') as string)?.toLowerCase() === 'usa'
                  ? 'Social Security Number'
                  : 'Tax Identification Number'
              }`}
            />
          ) : null}

          <div className="my-4 mr-4 w-full sm:my-0 lg:my-0">
            {props.getValues('investingProfile.option') !== InvestingPersonEnum.RETIREMENT ? (
              (props.getValues('addressInfo.country') as string).toLowerCase() === 'usa' ? (
                <FormLabel
                  labelName={`${
                    props.getValues('investingProfile.option') === InvestingPersonEnum.IN_BEHALF_OF_AN_ENTITY
                      ? 'Authorized Person'
                      : ''
                  } Social Security Number`}
                />
              ) : (
                <FormLabel
                  labelName={`${
                    props.getValues('investingProfile.option') === InvestingPersonEnum.IN_BEHALF_OF_AN_ENTITY
                      ? 'Authorized Person'
                      : ''
                  } Tax Identification Number`}
                />
              )
            ) : null}
          </div>

          {(props.getValues('addressInfo.country') as string).toLowerCase() === 'usa' ? (
            <InputField
              registerField={props.registerField}
              fieldId={'financialInfo.ssn'}
              validate={{
                minLength: (value: string) => value.length === 11,
                validateInput: (value: string) => {
                  return !value.includes('#')
                },
              }}
              control={props.control}
              maskedInput={'pattern'}
              format={'###-##-####'}
              value={props.getValues('financialInfo.ssn')}
              mask="#"
              maskType={'pattern'}
              allowEmptyFormatting={true}
              required={true}
              onChange={(e: any) => {
                props.clearErrors('financialInfo.ssn')
                e.formattedValue !== '###-##-####' && props.setValue('financialInfo.ssn', e.formattedValue)
              }}
            />
          ) : (
            <InputField
              required={true}
              registerField={props.registerField}
              fieldId={'financialInfo.tin'}
              control={props.control}
              maskedInput={'numeric'}
              value={props.getValues('financialInfo.tin')}
              maskType={'numeric'}
              allowEmptyFormatting={true}
              placeholder={'123456789'}
              onChange={(e: any) => {
                props.clearErrors('financialInfo.tin')
                props.setValue('financialInfo.tin', e.formattedValue)
              }}
            />
          )}

          {props.errors.financialInfo?.socialSecurityNumber && <ErrorMessage message={'Invalid input.'} />}
          {props.errors.financialInfo?.ssn?.type === 'minLength' && (
            <ErrorMessage message={'Invalid social security number.'} />
          )}
          {props.errors.financialInfo?.ssn?.type === 'validateInput' && (
            <ErrorMessage message={'Invalid social security number.'} />
          )}
          {props.errors.financialInfo?.ssn?.type === 'required' && (
            <ErrorMessage message={'Social Security Number is required.'} />
          )}
        </div>

        {/* -------------JOINTLY------------- */}

        {props.getValues('investingProfile.option') === InvestingPersonEnum.JOINTLY ? (
          <>
            <div className={`mb-2 mt-10 text-xl font-semibold md:mb-8 md:font-bold`}>Second Investor</div>

            <div className="my-4 mr-4 w-full lg:my-0">
              <FormLabel
                labelName={`${
                  props.getValues('investingProfile.option') === InvestingPersonEnum.IN_BEHALF_OF_AN_ENTITY
                    ? 'Authorized Person'
                    : ''
                } Date of Birth`}
              />

              <Controller
                control={props.control}
                name={'secondary.personalInfo.birthday'}
                rules={{
                  required: true,
                  validate: {
                    validateBday: (value: string) => moment().diff(new Date(value), 'years') >= 21,
                    invalidDate: (value: string) => moment(value).isValid(),
                  },
                }}
                render={({ field }: any) => (
                  <DatePicker
                    {...field}
                    format={'MM/DD/YYYY'}
                    onChange={onChangeBirthdaySecondary}
                    // onBlur={(e) => onBlurBirthdayChange(e, 'secondary.personalInfo.birthday')}
                    // onOpenChange={() => props.clearErrors('secondary.personalInfo.birthday')}
                    status={
                      props.errors?.secondary?.personalInfo?.birthday?.type === 'required' ||
                      props.errors?.secondary?.personalInfo?.birthday?.type === 'invalidDate' ||
                      props.errors?.secondary?.personalInfo?.birthday?.type === 'validateBday'
                        ? 'error'
                        : ''
                    }
                    disabledDate={disabledDate}
                    allowClear={false}
                    value={
                      !_.isEmpty(props.getValues('secondary.personalInfo.birthday'))
                        ? dayjs(props.getValues('secondary.personalInfo.birthday'))
                        : dayjs().subtract(21, 'years')
                    }
                    className="h-[42px] border-gray-300 bg-gray-50"
                  />
                )}
              />
              {props.errors?.secondary?.personalInfo?.birthday?.type === 'required' && (
                <ErrorMessage message={'Birthday is required.'} />
              )}
              {props.errors?.secondary?.personalInfo?.birthday?.type === 'validateBday' && (
                <ErrorMessage message={'Invalid date of birth. Must be at least 21 years old.'} />
              )}
              {props.errors?.secondary?.personalInfo?.birthday?.type === 'invalidDate' && (
                <ErrorMessage message={'Invalid date input.'} />
              )}
            </div>

            <div className="my-4 mr-4 w-full sm:my-0 lg:my-0">
              {(props.getValues('secondary.addressInfo.country') as string).toLowerCase() === 'usa' ? (
                <FormLabel
                  labelName={`${
                    props.getValues('investingProfile.option') === InvestingPersonEnum.IN_BEHALF_OF_AN_ENTITY
                      ? 'Authorized Person'
                      : ''
                  } Social Security Number`}
                />
              ) : (
                <FormLabel
                  labelName={`${
                    props.getValues('investingProfile.option') === InvestingPersonEnum.IN_BEHALF_OF_AN_ENTITY
                      ? 'Authorized Person'
                      : ''
                  } Tax Identification Number`}
                />
              )}
            </div>

            {(props.getValues('secondary.addressInfo.country') as string).toLowerCase() === 'usa' ? (
              <InputField
                registerField={props.registerField}
                fieldId={'secondary.financialInfo.ssn'}
                validate={{
                  minLength: (value: string) => value.length === 11,
                  validateInput: (value: string) => {
                    return !value.includes('#')
                  },
                }}
                control={props.control}
                maskedInput={'pattern'}
                format={'###-##-####'}
                value={props.getValues('secondary.financialInfo.ssn')}
                mask="#"
                maskType={'pattern'}
                allowEmptyFormatting={true}
                required={true}
                onChange={(e: any) => {
                  props.clearErrors('secondary.financialInfo.ssn')
                  e.formattedValue !== '###-##-####' && props.setValue('secondary.financialInfo.ssn', e.formattedValue)
                }}
              />
            ) : (
              <InputField
                required={true}
                registerField={props.registerField}
                fieldId={'secondary.financialInfo.tin'}
                control={props.control}
                maskedInput={'numeric'}
                value={props.getValues('secondary.financialInfo.tin')}
                maskType={'numeric'}
                allowEmptyFormatting={true}
                placeholder={'123456789'}
                onChange={(e: any) => {
                  props.clearErrors('secondary.financialInfo.tin')
                  props.setValue('secondary.financialInfo.tin', e.formattedValue)
                }}
              />
            )}

            {props.errors.financialInfo?.socialSecurityNumber && <ErrorMessage message={'Invalid input.'} />}
            {props.errors?.secondary?.financialInfo?.ssn?.type === 'minLength' && (
              <ErrorMessage message={'Invalid social security number.'} />
            )}
            {props.errors?.secondary?.financialInfo?.ssn?.type === 'validateInput' && (
              <ErrorMessage message={'Invalid social security number.'} />
            )}
            {props.errors?.secondary?.financialInfo?.ssn?.type === 'required' && (
              <ErrorMessage message={'Social Security Number is required.'} />
            )}
          </>
        ) : null}

        {props.getValues('investingProfile.option') === InvestingPersonEnum.RETIREMENT &&
          props.getValues('investingProfile.retirementAccountType') === RetirementAccountEnum.TRUST_ACCOUNT && (
            <div className="mt-4 text-base leading-5 text-gray-500">
              We are required to obtain this information for the trust’s authorized person. Just as if you were opening
              a bank or stock brokerage account for the trust, we are required by law to obtain your individual SSN or
              tax ID number for your country and your individual date of birth through this encrypted and SSL secure
              page for identity and age verification, investor screening and tax forms that may be associated with the
              investment.
            </div>
          )}

        {props.getValues('investingProfile.option') === InvestingPersonEnum.RETIREMENT &&
          props.getValues('investingProfile.retirementAccountType') === RetirementAccountEnum.RETIREMENT_ACCOUNT && (
            <div className="mt-4 text-base leading-5 text-gray-500">
              We are required to obtain this information for the retirement account holder. Just as if you were opening
              a bank or stock brokerage account for the retirement account, we are required by law to obtain your
              individual SSN or tax ID number for your country and your individual date of birth through this encrypted
              and SSL secure page for identity and age verification, investor screening and tax forms that may be
              associated with the investment.
            </div>
          )}

        {props.getValues('investingProfile.option') !== InvestingPersonEnum.RETIREMENT &&
          props.getValues('investingProfile.option') !== InvestingPersonEnum.IN_BEHALF_OF_AN_ENTITY && (
            <div className="mt-4 text-base leading-5 text-gray-500">
              Just as if{' '}
              {props.getValues('investingProfile.option') === InvestingPersonEnum.IN_BEHALF_OF_AN_ENTITY
                ? 'the company'
                : 'you'}{' '}
              were opening a bank or stock brokerage account, we are required by law to obtain your SSN or tax ID number
              for your country and your date of birth through this encrypted and SSL secure page for identity and age
              verification, investor screening and tax forms that may be associated with the investment.
            </div>
          )}

        {props.getValues('investingProfile.option') === InvestingPersonEnum.IN_BEHALF_OF_AN_ENTITY && (
          <div className="mt-4 text-base leading-5 text-gray-500">
            We are required to obtain this information for the company’s authorized person. Just as if you were opening
            a bank or stock brokerage account for the company, we are required by law to obtain your individual SSN or
            tax ID number for your country and your individual date of birth through this encrypted and SSL secure page
            for identity and age verification, investor screening and tax forms that may be associated with the
            investment.
          </div>
        )}

        <div className="w-full justify-center gap-3 py-10 sm:block lg:flex">
          <div className="flex flex-col sm:w-[100%] lg:w-[20%]">
            <Button btnName={'Back'} onClickHandler={() => props.previousStep('3')} />
          </div>
          <div className="flex flex-col sm:w-[100%] lg:w-[80%]">
            <Button
              btnName={'Next: Investment amount'}
              btnType={'button'}
              disabled={isSubmitDisabled()}
              onClickHandler={validateFields}
            />
          </div>
        </div>
      </div>
      <LoaderModal isVisible={isLoading} />
      <MessageModal
        isVisible={errorMessage !== ''}
        onProceed={() => {
          // automatically set the step to appropriate form duplicate error
          if (errorMessage === RiseUpErrors.DuplicatePhoneNumber + ' Please update the phone number.') {
            setErrorMessage('')
            props.nextStep('3')
          }
          if (errorMessage === RiseUpErrors.DuplicateSSN + ' Please update the ssn.') {
            setErrorMessage('')
            props.nextStep('4')
          }
          setErrorMessage('')
        }}
        onCancel={() => {
          setErrorMessage('')
        }}
        title={'Error'}
        description={errorMessage}
        type={'error'}
        btn2={'Ok'}
      />
    </div>
  )
}

export default SocialSecurityNum
